import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Col, Row, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// import { format } from 'date-fns';
import { FaCalendarAlt, FaPlus, FaTimes } from 'react-icons/fa';
import { useLabCurrency } from '../../../../hooks/useLabCurrency';

function InvoiceModal({ show, handleClose }) {
  // Lab's CURRENT currency (no hardcoded symbol)
  const { currencySymbol } = useLabCurrency();
  // State variables
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState(null);
  
  // Invoice items state (only relevant after account is selected)
  const [note, setNote] = useState('');
  const [lineItems, setLineItems] = useState([{ id: Date.now(), description: '', amount: '' }]);
  const [subtotal, setSubtotal] = useState(0);
  const [taxes, setTaxes] = useState(0);

  // Fetch accounts when modal opens
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoadingAccounts(true);
      setError(null);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const data = { 
          data: [
            { id: 'alaska_dental', account_name: 'Alaska House Dental' },
            { id: '1', account_name: 'Customer A' }, 
            { id: '2', account_name: 'Customer B' }
          ] 
        };
        setAccounts(data.data || []);
      } catch (fetchError) {
        console.error("Fetch Accounts Error:", fetchError);
        setError('Error fetching accounts.');
      } finally {
        setLoadingAccounts(false);
      }
    };
    if (show) fetchAccounts();
  }, [show]);

  // Calculate subtotal
  const calculateSubtotal = useCallback((items) => {
    let newSubtotal = 0;
    items.forEach(item => { newSubtotal += parseFloat(item.amount) || 0; });
    setSubtotal(newSubtotal);
  }, []);

  useEffect(() => { calculateSubtotal(lineItems); }, [lineItems, calculateSubtotal]);

  const total = (subtotal + taxes).toFixed(2);

  // Event handlers
  const handleAccountChange = (e) => {
    setSelectedAccount(e.target.value);
    // Reset invoice items when account changes
    setLineItems([{ id: Date.now(), description: '', amount: '' }]);
    setSubtotal(0);
    setTaxes(0);
    setNote('');
  };

  const handleLineItemChange = (id, field, value) => {
    setLineItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const handleDeleteLineItem = (id) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  const handleAddLineItem = () => {
    setLineItems(prev => [...prev, { id: Date.now(), description: '', amount: '' }]);
  };

  const handleSave = async () => {
    if (!selectedAccount || !invoiceDate) {
      alert('Please select an Account and Invoice Date.');
      return;
    }
    const itemsToSave = lineItems.filter(item => item.description.trim() !== '' || (parseFloat(item.amount) || 0) !== 0);
    if (itemsToSave.length === 0) {
      alert('Please add at least one invoice item.');
      return;
    }
    // Save logic here
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setInvoiceDate(new Date());
    setSelectedAccount('');
    setLineItems([{ id: Date.now(), description: '', amount: '' }]);
    setSubtotal(0);
    setTaxes(0);
    setNote('');
    setError(null);
    handleClose();
  };

  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <Form.Control type="text" value={value} onClick={onClick} ref={ref} readOnly />
  ));

  return (
    <Modal show={show} onHide={handleCloseModal} size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton style={{ backgroundColor: '#004aad', color: '#fff' }}>
        <Modal.Title style={{ fontWeight: 'bold' }}>New Invoice</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '1.5rem' }}>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <Form noValidate onSubmit={(e) => e.preventDefault()}>

          {/* --- Always show these basic details --- */}
          <h4 style={{ color: '#004aad', fontSize: '1.2rem', marginBottom: '1rem', fontWeight: 500 }}>
            Invoice Details
          </h4>
          
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group controlId="invoiceAccount">
                <Form.Label className="fw-bold">Account<span style={{ color: 'red' }}>*</span></Form.Label>
                <Form.Select 
                  value={selectedAccount} 
                  onChange={handleAccountChange} 
                  required 
                  disabled={loadingAccounts}
                >
                  <option value="">{loadingAccounts ? 'Loading...' : 'Select Account'}</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.account_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="invoiceDate">
                <Form.Label className="fw-bold">Invoice Date<span style={{ color: 'red' }}>*</span></Form.Label>
                <InputGroup>
                  <DatePicker 
                    selected={invoiceDate} 
                    onChange={(date) => setInvoiceDate(date)} 
                    dateFormat="d MMM yyyy" 
                    customInput={<CustomDateInput />} 
                  />
                  <InputGroup.Text style={{ backgroundColor: '#fff' }}>
                    <FaCalendarAlt style={{ color: '#004aad' }} />
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          {/* --- Only show invoice items after account is selected --- */}
          {selectedAccount && (
            <>
              <h4 style={{ color: '#004aad', fontSize: '1.2rem', marginBottom: '1rem', fontWeight: 500 }}>
                Invoice Items
              </h4>

              {/* Invoice items table */}
              {lineItems.map((item) => (
                <Row key={item.id} className="align-items-center mb-2">
                  <Col xs={12} md={7}>
                    <Form.Control
                      type="text"
                      placeholder="Item description"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                    />
                  </Col>
                  <Col xs={9} md={3}>
                    <InputGroup>
                      <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                      <Form.Control
                        type="number"
                        placeholder="0.00"
                        value={item.amount}
                        onChange={(e) => handleLineItemChange(item.id, 'amount', e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col xs={3} md={2}>
                    <Button variant="link" onClick={() => handleDeleteLineItem(item.id)}>
                      <FaTimes />
                    </Button>
                  </Col>
                </Row>
              ))}

              <Button 
                variant="primary" 
                onClick={handleAddLineItem}
                className="mt-2"
              >
                <FaPlus className="me-1" /> Add Item
              </Button>

              {/* Note and totals */}
              <Row className="mt-4">
                <Col md={7}>
                  <Form.Group controlId="invoiceNote">
                    <Form.Label className="fw-bold">Note</Form.Label>
                    <Form.Control as="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                    <Form.Text className="text-muted">
                      This note will be visible to customers
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-bold">Subtotal</span>
                    <span>{currencySymbol}{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-bold">Taxes</span>
                    <InputGroup size="sm" style={{ width: '120px' }}>
                      <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                      <Form.Control 
                        type="number" 
                        value={taxes || ''} 
                        onChange={(e) => setTaxes(parseFloat(e.target.value) || 0)}
                      />
                    </InputGroup>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold">Total</span>
                    <span className="fw-bold">{currencySymbol}{total}</span>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default InvoiceModal;